import React, { useEffect, useMemo, useRef, useState } from "react";
import { t } from "../i18n";
import {
  isPushSupported,
  getNotificationPermission,
  requestNotificationPermission,
  subscribeToPush,
  showLocalNotification
} from "../services/notifications";
import { fetchBillingStatus } from "../services/billing.js";
import "./Sidebar.css";

function Sidebar({ active }) {
  const [userLabel, setUserLabel] = useState("");
  const [creditsRemaining, setCreditsRemaining] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [open, setOpen] = useState(false);
  const dragRef = useRef(false);
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    try {
      const saved = window.localStorage.getItem("contentos_sidebar_width");
      return saved ? Number(saved) : 260;
    } catch {
      return 260;
    }
  });
  const [subTab, setSubTab] = useState("chat");
  const [chatInput, setChatInput] = useState("");
  const [chatMessages, setChatMessages] = useState([]);
  const [chatLoading, setChatLoading] = useState(false);
  const [chatSaving, setChatSaving] = useState(false);
  const [projectItems, setProjectItems] = useState([]);
  const [projectName, setProjectName] = useState("");
  const [projectDesc, setProjectDesc] = useState("");
  const [activeProjectId, setActiveProjectId] = useState(() => {
    try {
      return window.sessionStorage.getItem("contentos_active_project_id") || "";
    } catch {
      return "";
    }
  });
  const [serverActiveProjectId, setServerActiveProjectId] = useState("");
  const [settings, setSettings] = useState({
    language: "en",
    notion_token: "",
    notion_page: "",
    video_default_mode: "beginner"
  });
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [notionQuery, setNotionQuery] = useState("");
  const [notionResults, setNotionResults] = useState([]);
  const [notionLoading, setNotionLoading] = useState(false);
  const [archiveLoading, setArchiveLoading] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  // Notification preferences state
  const [notifPrefs, setNotifPrefs] = useState(() => {
    try {
      const saved = localStorage.getItem("notification_prefs");
      return saved ? JSON.parse(saved) : { push_enabled: false, email_enabled: false };
    } catch {
      return { push_enabled: false, email_enabled: false };
    }
  });
  const [notifPermission, setNotifPermission] = useState(() => getNotificationPermission());
  const [notifSupported] = useState(() => isPushSupported());

  useEffect(() => {
    let isMounted = true;
    async function loadUser() {
      try {
        const res = await fetch("/api/auth/me");
        if (!res.ok) throw new Error("no user");
        const data = await res.json();
        const user = data?.user;
        const name = user?.user_metadata?.name || user?.user_metadata?.display_name || "";
        const email = user?.email || "";
        const label = name || email || t("common.guest");
        if (isMounted) setUserLabel(label);
      } catch {
        if (isMounted) setUserLabel(t("common.guest"));
      }
    }
    loadUser();
    return () => {
      isMounted = false;
    };
  }, []);

  async function handleLogout(event) {
    event.preventDefault();
    event.stopPropagation();
    try {
      await fetch("/api/auth/logout", { method: "POST", credentials: "include" });
    } catch {
      // ignore
    }
    try {
      Object.keys(window.sessionStorage || {}).forEach((key) => {
        if (key.startsWith("contentos_")) window.sessionStorage.removeItem(key);
      });
      Object.keys(window.localStorage || {}).forEach((key) => {
        if (key.startsWith("contentos_")) window.localStorage.removeItem(key);
      });
    } catch {
      // ignore
    }
    window.location.href = "/";
  }

  useEffect(() => {
    let isMounted = true;
    async function loadProfile() {
      try {
        const res = await fetch("/api/fast/user/profile");
        if (!res.ok) throw new Error("no profile");
        const data = await res.json();
        const roles = Array.isArray(data.roles) ? data.roles : [];
        if (isMounted) setIsAdmin(roles.includes("admin"));
      } catch {
        if (isMounted) setIsAdmin(false);
      }
    }
    loadProfile();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;
    fetchBillingStatus()
      .then((data) => {
        if (!isMounted) return;
        setCreditsRemaining(typeof data.credits_remaining === "number" ? data.credits_remaining : null);
      })
      .catch(() => {
        if (!isMounted) return;
        setCreditsRemaining(null);
      });
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    try {
      const stored = window.sessionStorage.getItem("contentos_chat_history");
      if (stored) setChatMessages(JSON.parse(stored));
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    try {
      window.sessionStorage.setItem("contentos_chat_history", JSON.stringify(chatMessages));
    } catch {
      // ignore
    }
  }, [chatMessages]);

  useEffect(() => {
    async function loadProjects() {
      try {
        const res = await fetch("/api/fast/projects");
        if (!res.ok) return;
        const data = await res.json();
        setProjectItems(Array.isArray(data.items) ? data.items : []);
      } catch {
        setProjectItems([]);
      }
    }
    loadProjects();
  }, []);

  useEffect(() => {
    async function loadSettings() {
      try {
        const res = await fetch("/api/fast/user/settings");
        if (!res.ok) return;
        const data = await res.json();
        setSettings({
          language: data.language || "en",
          notion_token: data.notion_token || "",
          notion_page: data.notion_page || "",
          video_default_mode: data.video_default_mode || "beginner"
        });
        const fetchedActive = data.active_project_id || "";
        setServerActiveProjectId(fetchedActive);
        if (!activeProjectId && fetchedActive) {
          setActiveProjectId(fetchedActive);
          syncActiveProjectId(fetchedActive);
        } else if (activeProjectId && fetchedActive && activeProjectId !== fetchedActive) {
          persistActiveProject(activeProjectId);
        }
        setSettingsLoaded(true);
      } catch {
        // ignore
        setSettingsLoaded(true);
      }
    }
    loadSettings();
  }, []);

  const chatPlaceholder = useMemo(() => t("sidebar.chat.placeholder"), []);

  function syncActiveProjectId(projectId) {
    try {
      window.sessionStorage.setItem("contentos_active_project_id", projectId || "");
    } catch {
      // ignore
    }
  }

  async function persistActiveProject(projectId) {
    try {
      await fetch("/api/fast/user/settings", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ active_project_id: projectId })
      });
    } catch {
      // ignore
    }
  }

  function setActiveProject(projectId) {
    if (!projectId || projectId === activeProjectId) return;
    setActiveProjectId(projectId);
    setServerActiveProjectId(projectId);
    syncActiveProjectId(projectId);
    persistActiveProject(projectId);
  }

  function clearChatHistory() {
    setChatMessages([]);
    setChatInput("");
    try {
      window.sessionStorage.removeItem("contentos_chat_history");
    } catch {
      // ignore
    }
  }

  async function saveChatToNotion() {
    if (!chatMessages.length || chatSaving) return;
    setChatSaving(true);
    try {
      const payload = {
        messages: chatMessages,
        project_id: activeProjectId || null
      };
      const res = await fetch("/api/fast/chat/save_notion", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });
      await res.json();
    } catch {
      // ignore
    } finally {
      setChatSaving(false);
    }
  }

  async function sendChatMessage() {
    const trimmed = chatInput.trim();
    if (!trimmed || chatLoading) return;
    const updated = [...chatMessages, { role: "user", content: trimmed }];
    setChatMessages(updated);
    setChatInput("");
    setChatLoading(true);
    try {
      const res = await fetch("/api/fast/chat/complete", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ messages: updated })
      });
      const data = await res.json();
      if (!res.ok || !data.ok) throw new Error(data.error || "Erreur chat.");
      setChatMessages([...updated, { role: "assistant", content: data.content || "" }]);
    } catch (err) {
      setChatMessages([...updated, { role: "assistant", content: err.message || "Erreur." }]);
    } finally {
      setChatLoading(false);
    }
  }

  async function createProject() {
    if (!projectName.trim()) return;
    try {
      const res = await fetch("/api/fast/projects", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: projectName.trim(),
          description: projectDesc.trim()
        })
      });
      if (!res.ok) throw new Error("Erreur projet.");
      const data = await res.json();
      setProjectItems((prev) => [data.item, ...prev]);
      setActiveProject(data.item.project_id);
      setProjectName("");
      setProjectDesc("");
    } catch {
      // ignore
    }
  }

  async function archiveActiveProject() {
    if (!activeProjectId || archiveLoading) return;
    if (!window.confirm(t("sidebar.projects.archiveConfirm"))) return;
    setArchiveLoading(true);
    try {
      const res = await fetch("/api/fast/projects/archive", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ project_id: activeProjectId })
      });
      const data = await res.json();
      if (!res.ok || !data.ok) throw new Error("Erreur archive.");
      const remaining = projectItems.filter((project) => project.project_id !== activeProjectId);
      setProjectItems(remaining);
      const nextId = remaining[0]?.project_id || "";
      setActiveProjectId(nextId);
      setServerActiveProjectId(nextId);
      syncActiveProjectId(nextId);
      persistActiveProject(nextId);
    } catch {
      // ignore
    } finally {
      setArchiveLoading(false);
    }
  }

  async function saveSettings() {
    try {
      await fetch("/api/fast/user/settings", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(settings)
      });
      if (settings.language) {
        try {
          window.localStorage.setItem("contentos_lang", settings.language);
        } catch {
          // ignore
        }
        window.location.reload();
      }
    } catch {
      // ignore
    }
  }

  async function searchNotionPages() {
    const q = notionQuery.trim();
    if (q.length < 2) return;
    setNotionLoading(true);
    try {
      const res = await fetch(`/api/fast/notion/pages?query=${encodeURIComponent(q)}`);
      if (!res.ok) throw new Error("Erreur Notion.");
      const data = await res.json();
      setNotionResults(Array.isArray(data.items) ? data.items : []);
    } catch {
      setNotionResults([]);
    } finally {
      setNotionLoading(false);
    }
  }

  function selectNotionPage(page) {
    setSettings((prev) => ({ ...prev, notion_page: page.id }));
  }

  // Notification handlers
  async function togglePushNotifications() {
    if (notifPrefs.push_enabled) {
      // Disable
      const updated = { ...notifPrefs, push_enabled: false };
      setNotifPrefs(updated);
      localStorage.setItem("notification_prefs", JSON.stringify(updated));
      // Save to server
      try {
        await fetch("/api/fast/notifications/preferences", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(updated)
        });
      } catch { }
    } else {
      // Enable - request permission first
      const permission = await requestNotificationPermission();
      setNotifPermission(permission);
      if (permission === "granted") {
        try {
          await subscribeToPush();
          const updated = { ...notifPrefs, push_enabled: true };
          setNotifPrefs(updated);
          localStorage.setItem("notification_prefs", JSON.stringify(updated));
          // Save to server
          await fetch("/api/fast/notifications/preferences", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(updated)
          });
          // Test notification
          await showLocalNotification("Notifications activees", {
            body: "Vous recevrez des notifications pour vos generations.",
            tag: "notif-enabled"
          });
        } catch (err) {
          console.error("Erreur activation push:", err);
        }
      }
    }
  }

  async function toggleEmailNotifications() {
    const updated = { ...notifPrefs, email_enabled: !notifPrefs.email_enabled };
    setNotifPrefs(updated);
    localStorage.setItem("notification_prefs", JSON.stringify(updated));
    // Save to server
    try {
      await fetch("/api/fast/notifications/preferences", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(updated)
      });
    } catch { }
  }

  async function testNotification() {
    if (notifPermission !== "granted") {
      const permission = await requestNotificationPermission();
      setNotifPermission(permission);
      if (permission !== "granted") return;
    }
    await showLocalNotification("Test Notification", {
      body: "Les notifications fonctionnent correctement !",
      tag: "test-notif"
    });
  }

  useEffect(() => {
    const q = notionQuery.trim();
    if (q.length < 2) {
      setNotionResults([]);
      return undefined;
    }
    const handle = setTimeout(() => {
      searchNotionPages();
    }, 350);
    return () => clearTimeout(handle);
  }, [notionQuery]);

  useEffect(() => {
    function handleResize() {
      const mobile = window.matchMedia("(max-width: 960px)").matches;
      setIsMobile(mobile);
      if (!mobile) setOpen(true);
    }
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    function handleMove(event) {
      if (!dragRef.current) return;
      const minWidth = 220;
      const maxWidth = 380;
      const next = Math.min(maxWidth, Math.max(minWidth, event.clientX));
      setSidebarWidth(next);
      try {
        window.localStorage.setItem("contentos_sidebar_width", String(next));
      } catch {
        // ignore
      }
    }
    function handleUp() {
      if (!dragRef.current) return;
      dragRef.current = false;
      document.body.style.cursor = "";
    }
    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleUp);
    return () => {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleUp);
    };
  }, []);

  useEffect(() => {
    if (!settingsLoaded || !projectItems.length) return;
    const hasActive = activeProjectId && projectItems.some((project) => project.project_id === activeProjectId);
    if (hasActive) return;
    const fallbackId = projectItems[0].project_id;
    if (!fallbackId) return;
    setActiveProjectId(fallbackId);
    syncActiveProjectId(fallbackId);
    persistActiveProject(fallbackId);
  }, [activeProjectId, projectItems, settingsLoaded]);

  const activeProject = projectItems.find((project) => project.project_id === activeProjectId);
  const notionTarget = activeProject?.notion_page_id || settings.notion_page || "";
  const canSaveChat = Boolean(chatMessages.length && settings.notion_token && notionTarget && !chatSaving);

  return (
    <>
      <button
        className="sidebar-toggle"
        type="button"
        onClick={() => setOpen((prev) => !prev)}
      >
        {t("common.menu")}
      </button>
      {isMobile && open && <div className="sidebar-scrim" onClick={() => setOpen(false)} />}
      <aside
        className={`app-sidebar ${open ? "open" : "collapsed"}`}
        style={{ width: isMobile ? 260 : sidebarWidth }}
      >
        {!isMobile && (
          <div
            className="sidebar-resizer"
            onMouseDown={(event) => {
              event.preventDefault();
              dragRef.current = true;
              document.body.style.cursor = "col-resize";
            }}
          />
        )}
        <div className="sidebar-logo">
          <h1>Monke Art Flow</h1>
          <p>AI CREATIVE SUITE</p>
        </div>

        <div className="sidebar-user">
          <button
            type="button"
            className="sidebar-user-main"
            onClick={() => {
              window.location.href = "/pricing";
            }}
          >
            <span>{userLabel}</span>
            {typeof creditsRemaining === "number" && (
              <span className="sidebar-credits">
                <span className="material-symbols-rounded">paid</span>
                {creditsRemaining}
              </span>
            )}
          </button>
          <button
            type="button"
            className="sidebar-logout"
            onClick={handleLogout}
            aria-label="Se deconnecter"
            title="Se deconnecter"
          >
            <span className="material-symbols-rounded">logout</span>
          </button>
        </div>
        <div className="sidebar-active-project">
          <span className="sidebar-active-label">{t("sidebar.projects.active")}</span>
          <div className="sidebar-active-project-row">
            <strong>{activeProject?.name || activeProjectId || "-"}</strong>
            <button
              type="button"
              className="sidebar-active-project-switch"
              onClick={() => setSubTab("projects")}
              title={t("sidebar.projects.switch") || "Changer de projet"}
              aria-label={t("sidebar.projects.switch") || "Changer de projet"}
            >
              <span className="material-symbols-rounded">swap_horiz</span>
            </button>
          </div>
        </div>

        <div className="sidebar-section">
          <div className="sidebar-title">{t("sidebar.section.main")}</div>
          <nav className="sidebar-nav">
            <a className={active === "nano" ? "sidebar-link active" : "sidebar-link"} href="/nano">
              <span className="material-symbols-rounded sidebar-icon">auto_awesome</span>
              {t("sidebar.link.nano")}
            </a>
            <a className={active === "decor" ? "sidebar-link active" : "sidebar-link"} href="/decor">
              <span className="material-symbols-rounded sidebar-icon">palette</span>
              {t("sidebar.link.decor")}
            </a>

            {isAdmin && (
              <a className={active === "video" ? "sidebar-link active" : "sidebar-link"} href="/video">
                <span className="material-symbols-rounded sidebar-icon">videocam</span>
                {t("sidebar.link.video")}
              </a>
            )}
            {isAdmin && (
              <a className={active === "montage" ? "sidebar-link active" : "sidebar-link"} href="/montage">
                <span className="material-symbols-rounded sidebar-icon">video_library</span>
                {t("sidebar.link.montage")}
              </a>
            )}
            {isAdmin && (
              <a className={active === "tripo" ? "sidebar-link active" : "sidebar-link"} href="/tripo">
                <span className="material-symbols-rounded sidebar-icon">view_in_ar</span>
                {t("sidebar.link.tripo")}
              </a>
            )}
            {isAdmin && (
              <a className={active === "music" ? "sidebar-link active" : "sidebar-link"} href="/music">
                <span className="material-symbols-rounded sidebar-icon">music_note</span>
                {t("sidebar.link.music")}
              </a>
            )}
            <a className={active === "media" ? "sidebar-link active" : "sidebar-link"} href="/media">
              <span className="material-symbols-rounded sidebar-icon">photo_library</span>
              {t("sidebar.link.media")}
            </a>
            <a className={active === "projects" ? "sidebar-link active" : "sidebar-link"} href="/projects">
              <span className="material-symbols-rounded sidebar-icon">folder_open</span>
              {t("sidebar.link.projects")}
            </a>
          </nav>
        </div>

        <div className="sidebar-section">
          <div className="sidebar-title">{t("sidebar.section.streamlit")}</div>
          <div className="sidebar-tabs">
            <button
              className={subTab === "chat" ? "sidebar-tab active" : "sidebar-tab"}
              onClick={() => setSubTab("chat")}
              title={t("sidebar.link.chat")}
            >
              <span className="material-symbols-rounded">chat</span>
            </button>
            <button
              className={subTab === "projects" ? "sidebar-tab active" : "sidebar-tab"}
              onClick={() => setSubTab("projects")}
              title={t("sidebar.link.projects")}
            >
              <span className="material-symbols-rounded">folder_open</span>
            </button>
            <button
              className={subTab === "config" ? "sidebar-tab active" : "sidebar-tab"}
              onClick={() => setSubTab("config")}
              title={t("sidebar.link.config")}
            >
              <span className="material-symbols-rounded">tune</span>
            </button>
          </div>

          <div className="sidebar-panel">
            {subTab === "chat" && (
              <div className="sidebar-chat">
                <div className="sidebar-chat-history">
                  {chatMessages.length === 0 && (
                    <div className="sidebar-chat-empty">{t("sidebar.chat.empty")}</div>
                  )}
                  {chatMessages.map((msg, idx) => (
                    <div
                      key={`${msg.role}-${idx}`}
                      className={msg.role === "user" ? "chat-bubble user" : "chat-bubble assistant"}
                    >
                      {msg.content}
                    </div>
                  ))}
                </div>
                <div className="sidebar-chat-input">
                  <textarea
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    rows={3}
                    placeholder={chatPlaceholder}
                  />
                  <div className="sidebar-chat-actions">
                    <button className="sidebar-action" onClick={sendChatMessage} disabled={chatLoading}>
                      {chatLoading ? t("sidebar.chat.sending") : t("sidebar.chat.send")}
                    </button>
                    <button
                      className="sidebar-action secondary"
                      onClick={clearChatHistory}
                      disabled={!chatMessages.length}
                      title={t("sidebar.chat.clear")}
                      aria-label={t("sidebar.chat.clear")}
                    >
                      <span className="material-symbols-rounded">delete</span>
                    </button>
                    <button
                      className="sidebar-action secondary"
                      onClick={saveChatToNotion}
                      disabled={!canSaveChat}
                      title={t("sidebar.chat.save_notion")}
                      aria-label={t("sidebar.chat.save_notion")}
                    >
                      <span className="material-symbols-rounded">note_add</span>
                    </button>
                  </div>
                </div>
              </div>
            )}

            {subTab === "projects" && (
              <div className="sidebar-projects">
                <div className="sidebar-project-list">
                  {projectItems.length === 0 && (
                    <div className="sidebar-chat-empty">{t("sidebar.projects.empty")}</div>
                  )}
                  {projectItems.map((project) => {
                    const isActive = project.project_id === activeProjectId;
                    return (
                      <button
                        key={project.project_id}
                        type="button"
                        className={isActive ? "sidebar-project-item active" : "sidebar-project-item"}
                        onClick={() => setActiveProject(project.project_id)}
                        aria-pressed={isActive}
                      >
                        <strong>{project.name}</strong>
                        <span>{project.description || project.type}</span>
                      </button>
                    );
                  })}
                </div>
                <div className="sidebar-project-actions">
                  <button
                    className="sidebar-action danger"
                    onClick={archiveActiveProject}
                    disabled={!activeProjectId || archiveLoading}
                  >
                    {archiveLoading ? t("sidebar.projects.archiving") : t("sidebar.projects.archive")}
                  </button>
                </div>
                <div className="sidebar-project-form">
                  <input
                    type="text"
                    placeholder={t("sidebar.projects.name")}
                    value={projectName}
                    onChange={(e) => setProjectName(e.target.value)}
                  />
                  <input
                    type="text"
                    placeholder={t("sidebar.projects.desc")}
                    value={projectDesc}
                    onChange={(e) => setProjectDesc(e.target.value)}
                  />
                  <button className="sidebar-action" onClick={createProject}>
                    {t("sidebar.projects.create")}
                  </button>
                </div>
              </div>
            )}

            {subTab === "config" && (
              <div className="sidebar-config">
                <label>
                  {t("sidebar.config.language")}
                  <select
                    value={settings.language}
                    onChange={(e) => setSettings((prev) => ({ ...prev, language: e.target.value }))}
                  >
                    <option value="en">English</option>
                    <option value="fr">Francais</option>
                  </select>
                </label>
                <label>
                  {t("sidebar.config.notionToken")}
                  <input
                    type="password"
                    value={settings.notion_token}
                    onChange={(e) => setSettings((prev) => ({ ...prev, notion_token: e.target.value }))}
                  />
                </label>
                <label>
                  {t("sidebar.config.videoMode")}
                  <select
                    value={settings.video_default_mode}
                    onChange={(e) =>
                      setSettings((prev) => ({ ...prev, video_default_mode: e.target.value }))
                    }
                  >
                    <option value="beginner">{t("sidebar.config.videoModeBeginner")}</option>
                    <option value="expert">{t("sidebar.config.videoModeExpert")}</option>
                  </select>
                </label>
                <div className="sidebar-notion-search">
                  <label>
                    {t("sidebar.config.notionSearch")}
                    <input
                      type="text"
                      value={notionQuery}
                      onChange={(e) => setNotionQuery(e.target.value)}
                      placeholder={t("sidebar.config.notionSearchPlaceholder")}
                    />
                  </label>
                  <div className="sidebar-project-list">
                    {notionResults.map((page) => (
                      <button
                        key={page.id}
                        type="button"
                        className="sidebar-project-item"
                        onClick={() => selectNotionPage(page)}
                      >
                        <strong>{page.title}</strong>
                        <span>{page.id}</span>
                      </button>
                    ))}
                  </div>
                </div>
                <label>
                  {t("sidebar.config.notionPage")}
                  <input
                    type="text"
                    value={settings.notion_page}
                    onChange={(e) => setSettings((prev) => ({ ...prev, notion_page: e.target.value }))}
                  />
                </label>
                <button className="sidebar-action" onClick={saveSettings}>
                  {t("sidebar.config.save")}
                </button>

                {/* Notification Preferences */}
                <div className="notification-prefs">
                  <h4>
                    <span className="material-symbols-rounded" style={{ fontSize: 16, marginRight: 6 }}>notifications</span>
                    {t("sidebar.config.notifications") || "Notifications"}
                  </h4>

                  {/* Push notifications toggle */}
                  <div className="notification-pref-row">
                    <label>
                      <span className="material-symbols-rounded">phone_android</span>
                      {t("sidebar.config.pushNotif") || "Push mobile"}
                    </label>
                    <button
                      type="button"
                      className={`notification-toggle ${notifPrefs.push_enabled ? "active" : ""}`}
                      onClick={togglePushNotifications}
                      disabled={!notifSupported}
                      aria-pressed={notifPrefs.push_enabled}
                    />
                  </div>

                  {/* Email notifications toggle */}
                  <div className="notification-pref-row">
                    <label>
                      <span className="material-symbols-rounded">email</span>
                      {t("sidebar.config.emailNotif") || "Email"}
                    </label>
                    <button
                      type="button"
                      className={`notification-toggle ${notifPrefs.email_enabled ? "active" : ""}`}
                      onClick={toggleEmailNotifications}
                      aria-pressed={notifPrefs.email_enabled}
                    />
                  </div>

                  {/* Permission status */}
                  <div className={`notification-status ${notifPermission}`}>
                    {notifPermission === "granted" && (t("sidebar.config.notifGranted") || "Notifications autorisees")}
                    {notifPermission === "denied" && (t("sidebar.config.notifDenied") || "Notifications bloquees dans le navigateur")}
                    {notifPermission === "default" && (t("sidebar.config.notifDefault") || "Cliquez pour autoriser les notifications")}
                    {notifPermission === "unsupported" && (t("sidebar.config.notifUnsupported") || "Non supporte par ce navigateur")}
                  </div>

                  {/* Test button */}
                  {notifSupported && notifPermission === "granted" && (
                    <button
                      className="sidebar-action secondary"
                      onClick={testNotification}
                      style={{ marginTop: 8, fontSize: 11 }}
                    >
                      <span className="material-symbols-rounded" style={{ fontSize: 14, marginRight: 4 }}>science</span>
                      {t("sidebar.config.testNotif") || "Tester"}
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </aside>
    </>
  );
}

export default Sidebar;
